import { Box } from "@mui/material";
import { Fragment } from "react/jsx-runtime";

import { useAppSelector } from "~/app/store";
import {
  mixpanelTrackPickWrapper,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { selectServerConfig } from "~/redux/selectors/appSelectors";

import { useAddedTotes } from "./hooks/useAddedTotes";
import AutostoreTote from "./tote/AutostoreTote";

type AutostorePickingTotesProps = {
  handleToteClick: (args: {
    toteId: string;
    pickId: string;
    reporter?: string;
  }) => Promise<void>;
};

export const AutostorePickingTotes = ({
  handleToteClick
}: AutostorePickingTotesProps) => {
  const { ap_fusionPortScreenEnabled, ap_totesPlacement } =
    useAppSelector(selectServerConfig);
  const { originalTotes, addedTotes, focusedPick } = useAddedTotes();
  const trackedPageName = useMixpanelPageName();

  return (
    <Box
      display="grid"
      sx={{
        // "right" view is 4 wide, split view is 2 wide with spacer column
        gridTemplateColumns:
          ap_totesPlacement === "split" ? "1fr 2fr 1fr" : "1fr 1fr 1fr 1fr",
        // right view is 2 rows tall, split view is 3 tall
        gridTemplateRows:
          ap_totesPlacement === "split" ? "1fr 1fr 1fr" : "1fr 1fr",
        rowGap: 1.5,
        width: "100%"
      }}
    >
      {originalTotes.map((tote, i) => {
        const allTotesAtPosition = addedTotes
          // select all added totes that are in our position
          .filter((addedTote) => addedTote.oldToteId === tote.toteId)
          .map((at) => ({
            toteId: at.toteId,
            externalToteId: at.externalToteId
          }));

        // add the original tote at the back
        allTotesAtPosition.unshift({
          toteId: tote.toteId,
          externalToteId: tote.externalToteId
        });

        // move the currently assigned tote to the front of the tote stack.
        allTotesAtPosition.sort((a, b) => {
          if (focusedPick?.assignedToteId === a.toteId) {
            return 1;
          }
          if (focusedPick?.assignedToteId === b.toteId) {
            return -1;
          }
          return 0;
        });

        const pickClickCallback = async () => {
          if (!focusedPick || !focusedPick.assignedToteId) return;
          mixpanelTrackPickWrapper(
            trackedPageName,
            "Button Click",
            "Confirm Pick (Tote)",
            i + 1
          );
          await handleToteClick({
            toteId: focusedPick.assignedToteId,
            pickId: focusedPick.pickId
          });
        };

        return (
          <Fragment key={i}>
            <Box
              style={{
                width: "100%",
                marginBottom: 0,
                position: "relative",
                minHeight: ap_fusionPortScreenEnabled ? "300px" : "370px"
              }}
            >
              {allTotesAtPosition.map((currentTote, index) => {
                const isActiveTote =
                  currentTote.toteId === focusedPick?.assignedToteId;
                const leftPosition = 50 + index;
                const topPosition = index * 2;
                const isLastStackedTote =
                  index === allTotesAtPosition.length - 1;

                return (
                  <Box
                    key={`toteId-${currentTote.toteId}`}
                    style={{
                      position: "absolute",
                      left: `${leftPosition}%`,
                      transform: isLastStackedTote
                        ? "translateX(-50%)"
                        : "translateX(-76%)",
                      width: isLastStackedTote ? "100%" : "186px",
                      marginTop: isLastStackedTote
                        ? `${topPosition}%`
                        : `${topPosition + 4}%`
                    }}
                  >
                    <AutostoreTote
                      toteId={currentTote.toteId}
                      position={i + 1}
                      externalToteId={currentTote.externalToteId}
                      isActiveTote={isActiveTote}
                      onClickCb={pickClickCallback}
                      isLastStackedTote={isLastStackedTote}
                    />
                  </Box>
                );
              })}
            </Box>
            {/* create space for bin/product column */}
            {ap_totesPlacement === "split" && (i + 1) % 2 === 1 && <Box />}
          </Fragment>
        );
      })}
    </Box>
  );
};
